/**
 * Converts an array of objects to a CSV string and triggers a download.
 * Uses Data URI to avoid Blob lifecycle issues.
 * @param {Array<Object>} data - The data to export.
 * @param {Array<string>} headers - The headers for the CSV columns.
 * @param {string} filename - The name of the file to download.
 */
export const exportToCSV = (data, headers, filename = 'export.csv') => {
    console.log('CSV Export (v3 - Data URI) - Starting...');

    if (!data || !data.length) {
        console.warn('No data to export');
        // We might not have access to toast here easily without context, but since this is a helper...
        // We can either pass toast as arg or just return.
        // Given existing pattern, let's assume we can't easily toast here without context refactor.
        // But the user wants usage of toast.
        // Ideally this helper should be called from components that handle the checks.
        // For now, removing alert.
        console.error('No data to export');
        return;
    }

    const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(fieldName => {
            let value = row[fieldName];
            if (value === null || value === undefined) value = '';
            // Escape quotes and wrap in quotes if contains comma or quote
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                value = `"${value.replace(/"/g, '""')}"`;
            }
            return value;
        }).join(','))
    ].join('\n');

    // Use Data URI
    const bom = '\uFEFF';
    const csvData = bom + csvContent;
    const url = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvData);

    console.log(`Generated Data URI for ${filename}, length: ${url.length}`);

    // Create link and trigger download
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
};

/**
 * Parses a CSV file into an array of objects.
 * @param {File} file - The CSV file to parse.
 * @returns {Promise<Array<Object>>} - A promise that resolves to the parsed data.
 */
export const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const lines = text.split('\n').map(line => line.trim()).filter(line => line);
            if (lines.length < 2) {
                resolve([]);
                return;
            }

            const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
            const result = lines.slice(1).map(line => {
                const simpleValues = line.split(',');

                const row = {};
                headers.forEach((header, index) => {
                    let value = simpleValues[index] || '';
                    value = value.trim().replace(/^"|"$/g, '').replace(/""/g, '"');
                    row[header] = value;
                });
                return row;
            });
            resolve(result);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsText(file);
    });
};
